/**
 * BookingPageSkeleton — loading skeleton for the public booking page.
 *
 * Mirrors the live three-column layout (BookingHostCard | calendar | slots)
 * exactly, including:
 *   - host-card left column with avatar + name + title + description rows
 *     + metadata rows under a divider
 *   - calendar middle: month header with nav arrows, weekday row, 6×7 grid
 *     of rounded-square date tiles (matches the new rounded-lg tile style;
 *     the old skeleton drew circles)
 *   - slot list right: date header + TZ caption + 12h/24h toggle, then a
 *     vertical column of pill placeholders (matches the redesigned single-
 *     column list; the old skeleton drew a 2–3 column grid)
 *   - design-system tokens (`var(--color-bg-tertiary)`) instead of the
 *     legacy DaisyUI `bg-base-300` so the placeholder colour matches the
 *     rest of the redesigned chrome
 *
 * The skeleton is also used by RescheduleBookingPage during its initial
 * fetch — a small banner appears when content loads but the underlying
 * grid matches, so the transition stays mostly jitter-free.
 */

import { cx } from '@/utils/cx';

const SHIMMER = 'bg-[var(--color-bg-tertiary)]';
const SHIMMER_FADED = 'bg-[var(--color-bg-tertiary)]/50';

// 6 weeks × 7 days = 42 cells. First and last rows include faded
// adjacent-month days so the grid reads as a real calendar.
const DAY_VARIANTS: Array<'normal' | 'faded'> = [
  ...(['faded', 'faded', 'faded'] as const),
  ...Array(32).fill('normal' as const),
  ...([
    'normal',
    'faded',
    'faded',
    'faded',
    'faded',
    'faded',
    'faded',
  ] as const),
];

function HostCardSkeleton() {
  return (
    <aside className='flex flex-col gap-3 md:gap-4 md:border-r md:border-[var(--color-border-secondary)] md:pr-6 lg:pr-10'>
      {/* Avatar + display name — horizontal on mobile (matches the live
          compact layout), vertical on md+. The live BookingHostCard uses
          `size='lg'` (48px) consistently on the Avatar (see cubic review
          fix), so the skeleton must do the same — not the previous
          `size-10 md:size-12` responsive shape, which left an 8px shorter
          avatar visible on mobile during the skeleton paint and caused a
          jump when the real avatar slotted in. */}
      <div className='flex items-center gap-3 md:flex-col md:items-start md:gap-2'>
        <div className={cx('size-12 shrink-0 rounded-full', SHIMMER)} />
        <div className={cx('h-4 w-24 rounded', SHIMMER)} />
      </div>
      {/* Event title — live uses text-xl md:text-2xl with leading-tight.
          text-xl ≈ 25px line-height, text-2xl ≈ 30px, so h-6/h-7 are the
          closest 4px-grid matches. */}
      <div className={cx('h-6 w-3/4 rounded md:h-7', SHIMMER)} />
      {/* Description — two lines of text-sm leading-relaxed in the live
          layout. text-sm font-size 14px × leading-relaxed 1.625 ≈ 22.75px
          per line × 2 lines = 45.5px total. The bars themselves stay at
          h-4 (16px) so they read as text placeholders, and the row spacing
          (space-y-3.5 = 14px) plus bar heights total ~46px — within 0.5px
          of the live block, so no visible vertical jump when the real
          description slots in. The old h-3.5 + space-y-2 was 36px, off by
          ~10px and causing the host column to grow visibly on first paint. */}
      <div className='space-y-3.5 hidden md:block'>
        <div className={cx('h-4 w-full rounded', SHIMMER)} />
        <div className={cx('h-4 w-5/6 rounded', SHIMMER)} />
      </div>
      {/* Divider + metadata rows */}
      <div className='mt-1 flex flex-col gap-2 border-t border-[var(--color-border-secondary)] pt-4'>
        {[16, 20, 24].map(w => (
          <div key={w} className='flex items-center gap-2'>
            <div className={cx('size-4 rounded', SHIMMER)} />
            <div
              className={cx('h-3.5 rounded', SHIMMER)}
              style={{ width: `${w * 4}px` }}
            />
          </div>
        ))}
      </div>
    </aside>
  );
}

function CalendarGridSkeleton() {
  return (
    <div className='flex-1'>
      {/* Month header — "May 2026" + chevron nav buttons.
          Live uses an <h2 className='text-lg font-semibold'> (text-lg has
          a default 28px line-height) so the skeleton bar is h-7 to match.
          The nav buttons are <Button size='sm' iconLeading={Chevron…}>:
          icon-only sm buttons render at 36×36 with rounded-lg corners
          (`p-2` + 20px icon = 36px; class string includes `rounded-lg`),
          so h-9 w-9 rounded-lg matches both dimensions and corner radius. */}
      <div className='mb-4 flex items-center justify-between'>
        <div className={cx('h-7 w-32 rounded', SHIMMER)} />
        <div className='flex gap-1'>
          <div className={cx('h-9 w-9 rounded-lg', SHIMMER)} />
          <div className={cx('h-9 w-9 rounded-lg', SHIMMER)} />
        </div>
      </div>
      {/* Weekday row — live cells are h-10 (40px) with text-sm centered.
          The previous h-8 (32px) left an 8px gap between the skeleton's
          weekday band and the real one, visible as the date grid jumping
          up by a row on first paint. */}
      <div className='mb-2 grid grid-cols-7 gap-1'>
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key -- static placeholder
            key={`weekday-${i}`}
            className='flex h-10 items-center justify-center'
          >
            <div className={cx('h-3 w-5 rounded', SHIMMER)} />
          </div>
        ))}
      </div>
      {/* Date grid — rounded squares matching the live CalendarDay style
          (h-9 w-9 sm:h-10 sm:w-10 rounded-lg). */}
      <div className='grid grid-cols-7 gap-1'>
        {DAY_VARIANTS.map((variant, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key -- static placeholder
            key={`day-${i}`}
            className={cx(
              'h-9 w-9 rounded-lg sm:h-10 sm:w-10',
              variant === 'normal' ? SHIMMER : SHIMMER_FADED
            )}
          />
        ))}
      </div>
    </div>
  );
}

function SlotListSkeleton() {
  // Mirrors BookingCalendar's right-column structure exactly:
  //   outer: flex flex-1 flex-col lg:max-w-xs        (no gap)
  //     header: mb-4 (provides 16px to first pill)
  //     pills: flex flex-col gap-2                   (8px between pills)
  //
  // The previous skeleton put gap-2 on the OUTER container, so the spacing
  // between the header and the first pill became gap-2 + mb-4 = 24px,
  // versus the live's 16px. That extra 8px pushed pills 1–8 down and
  // grew the right column visibly when the loaded layout slotted in.
  return (
    <div className='flex flex-1 flex-col lg:max-w-xs'>
      {/* Date header + TZ caption (left) and 12h/24h toggle (right).
          Live uses <h3 className='text-lg font-semibold'> (28px line) for
          the date and <span className='text-xs'> (16px line) for the TZ
          caption, so h-7 and h-4 are the closest 4px-grid matches. The
          previous h-5 + h-3 was 8px shorter overall and visibly shifted
          the slot pill list up when the real header rendered.
          Mobile stacks header above toggle to match the live behaviour. */}
      <div className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3'>
        <div className='flex min-w-0 flex-col gap-1'>
          <div className={cx('h-7 w-40 rounded', SHIMMER)} />
          <div className={cx('h-4 w-28 rounded', SHIMMER)} />
        </div>
        <div className={cx('h-7 w-16 rounded-md', SHIMMER)} />
      </div>
      {/* Slot pills — single-column vertical list, full-width. The live
          slot list now uses a FIXED 296px height (h-[296px]) so the right
          column doesn't shrink/grow as the booker clicks between days
          with different slot counts. Each pill is 42px tall (text-sm
          20px line + py-2.5 20px padding + 2px border). With 6 pills +
          5 × gap-2 (8px) we fill 6×42 + 40 = 292px — 4px shy of the
          296px box, which mirrors what the live calendar looks like
          when the auto-selected day has 6 slots (the most common
          first-paint scenario).
          The h-[296px] wrapper is what stops the skeleton column from
          driving a card-height jump when the populated calendar slots
          in — both states reserve identical vertical space. */}
      <div className='flex h-[296px] flex-col gap-2'>
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key -- static placeholder
            key={`slot-${i}`}
            className={cx('h-[42px] rounded-lg', SHIMMER)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton loader for the booking page (and the reschedule page in its
 * pre-fetch state). Matches the real layout cell-for-cell so the loaded
 * content slots in without jitter.
 */
export function BookingPageSkeleton() {
  return (
    <div
      // `lg:min-h-[420px]` mirrors the live calendar + form steps' grids so
      // the skeleton → live transition doesn't grow the card mid-paint. See
      // the matching comment in BookingPage's renderStepContent.
      className='grid animate-pulse grid-cols-1 gap-6 md:grid-cols-[260px_minmax(0,1fr)] lg:min-h-[420px] lg:gap-10'
      role='status'
      aria-label='Loading booking page'
    >
      <HostCardSkeleton />
      <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
        <CalendarGridSkeleton />
        <SlotListSkeleton />
      </div>
      <span className='sr-only'>Loading booking page…</span>
    </div>
  );
}
